import { Box, Collapse, FormLabel, ListItem, TextField } from "@mui/material";
import { ChangeEvent, HTMLInputTypeAttribute, useState } from "react";
import { useTranslation } from "react-i18next";

import AutoCompleteSelect from "../../../../../components/autocomplete/autoCompleteSelect/AutoCompleteSelect";
import TagTextField from "../../../../../components/autocomplete/tagTextField/TagTextField";
import CollapsibleList from "../../../../../components/collapsibleList/CollapsibleList";
import { useOrderContext } from "../../../../../contexts/OrderContext";
import useOwnersQuery from "../../../../../hooks/graphql-hooks/queries/useOwnersQuery";
import { useNewOrderQueryData } from "../../../../../hooks/useNewOrderQueryData";
import { OwnerFragment } from "../../../../../models/autogenerated-deliveryService-gql/graphql";
import { IDeliveryItem } from "../../../../../models/deliveryItem";
import { RoutePath } from "../../../../../models/routePath";
import { isValidString } from "../../../../../utils/helper";
import "./AddDeliveryItemForm.scss";
import useDeliveryItemFormValidation from "./useDeliveryItemFormValidation";

interface AddDeliveryItemFormProps {
  formData: IDeliveryItem;
  existingPositions: number[];
  onChange: (fieldID: string, text: string) => void;
  setIsFormValid: (isValid: boolean) => void;
}

const disabledFieldsForConfirmQuantities: (keyof IDeliveryItem)[] = [
  "position",
  "material",
  "quantity",
  "unitOfMeasurement",
  "stockType",
  "itemCategory",
];

const AddDeliveryItemForm = (props: AddDeliveryItemFormProps) => {
  const { t } = useTranslation();
  const { queryData } = useNewOrderQueryData();
  const { generalInfo } = useOrderContext().savedProgress;

  const { formData, existingPositions, onChange, setIsFormValid } = props;

  //we want to disable fields in the form if we are on the confirm quantities page
  const disabledFields = window.location.pathname.includes(
    RoutePath.ConfirmQuantities,
  )
    ? disabledFieldsForConfirmQuantities
    : undefined;

  useOwnersQuery({
    locationId: generalInfo?.locationId ?? "",
    dataAction: (collection) => setOwners(collection.data),
  });

  const { positionError, validatePosition } = useDeliveryItemFormValidation({
    formData,
    existingPositions,
    setIsFormValid,
  });

  const [openRequired, setOpenRequired] = useState(true);
  const [openOptional, setOpenOptional] = useState(true);
  const [owners, setOwners] = useState<OwnerFragment[]>();

  const selectedMaterialObj = queryData.materials?.find(
    (m) => m.name === formData.material,
  );

  const materialOptions = queryData.materials
    ?.map((l) => l.name)
    .filter(isValidString);

  const stockTypeOptions = queryData.stockTypes
    ?.map((l) => l.longName)
    .filter(isValidString);

  const batchOptions = selectedMaterialObj?.batchesResolved
    ?.map((b) => b?.name)
    .filter(isValidString);

  const unitOptions = selectedMaterialObj?.units
    .map((u) => u.unitOfMeasurement)
    .filter(isValidString);

  const ownerOptions = owners?.map((l) => l.name).filter(isValidString);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    if (id === "position") validatePosition(value);
    onChange(id, value);
  };

  const renderTextField = (
    label: keyof IDeliveryItem,
    required?: boolean,
    type?: HTMLInputTypeAttribute,
    error?: string,
  ) => (
    <div style={{ width: "100%" }}>
      <FormLabel required={required}>{t(label)}</FormLabel>
      <TextField
        type={type}
        data-testid={`delivery-item-form-textfield-${label}`}
        id={label}
        className="input-element"
        value={formData[label]}
        onChange={handleChange}
        variant="outlined"
        required={required}
        error={!!error}
        helperText={error}
        disabled={disabledFields?.includes(label)}
      />
    </div>
  );

  const renderAutoCompleteSelect = (
    label: keyof IDeliveryItem,
    options: string[],
    required?: boolean,
  ) => (
    <AutoCompleteSelect
      fieldId={label}
      options={options}
      onSelect={onChange}
      initialValue={formData[label]}
      required={required}
      disabled={disabledFields?.includes(label)}
    />
  );

  return (
    <Box className="item-form-container" component="form" autoComplete="off">
      <CollapsibleList
        title={t("requiredInformation")}
        openCollapsible={openRequired}
        onClick={() => setOpenRequired((prev) => !prev)}
      >
        <ListItem>
          {renderTextField("position", true, "number", positionError)}
        </ListItem>
        <ListItem>
          {renderAutoCompleteSelect("material", materialOptions || [], true)}
        </ListItem>
        <Collapse in={!!formData.material} timeout="auto" unmountOnExit>
          <ListItem>
            <div className="item-form-container-item-row">
              {renderTextField("quantity", true, "number")}
              {renderAutoCompleteSelect(
                "unitOfMeasurement",
                unitOptions ?? [],
                true,
              )}
            </div>
          </ListItem>
        </Collapse>
        <ListItem>
          {renderAutoCompleteSelect("stockType", stockTypeOptions || [], true)}
        </ListItem>
      </CollapsibleList>
      <CollapsibleList
        title={t("optionalInformation")}
        openCollapsible={openOptional}
        onClick={() => setOpenOptional((prev) => !prev)}
      >
        <ListItem>{renderTextField("itemCategory", false)}</ListItem>
        <ListItem>
          {renderAutoCompleteSelect("owner", ownerOptions ?? [], false)}
        </ListItem>
        <ListItem>
          <TagTextField
            fieldId={"serialNumbers"}
            onChange={onChange}
            initialValue={
              formData.serialNumbers.length > 0
                ? formData.serialNumbers.split(",")
                : []
            }
          />
        </ListItem>
        <Collapse in={!!formData.material} timeout="auto" unmountOnExit>
          <ListItem>
            {renderAutoCompleteSelect("batch", batchOptions ?? [], false)}
          </ListItem>
        </Collapse>
      </CollapsibleList>
    </Box>
  );
};
export default AddDeliveryItemForm;
